/**
 * Selling recommendations — what Talab may SUGGEST with or instead of an item.
 *
 * Deliberately not a rule engine. The merchant only picks WHAT to recommend and
 * whether it's a better option or an addition; Talab decides when to raise it
 * and how to phrase it. Everything here is per-trade, so a perfume house is
 * never offered "airport delivery" and a restaurant is never offered "gift
 * wrapping for a villa".
 */

export type RecoKind = 'better' | 'addon'

export interface RecoCandidate {
  id: string
  name: string
  /** Displayed as-is; an add-on may be free ("Included"). */
  price: string
  /** The one line under the name — category, size, or what it is. */
  note: string
  image: string
  /** Why Talab would raise it. Auto-written, merchant-editable. */
  reason: string
}

export interface Reco extends RecoCandidate {
  kind: RecoKind
  active: boolean
}

const IMG = {
  landcruiser:
    'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&q=80&w=400',
  patrol:
    'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?auto=format&fit=crop&q=80&w=400',
  camry:
    'https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?auto=format&fit=crop&q=80&w=400',
  service:
    'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&q=80&w=400',
  seat: 'https://images.unsplash.com/photo-1591370874773-6702e8f12fd8?auto=format&fit=crop&q=80&w=400',
  airport:
    'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&q=80&w=400',
  shirt:
    'https://images.unsplash.com/photo-1602810318383-e386cc2a3ccf?auto=format&fit=crop&q=80&w=400',
  belt: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?auto=format&fit=crop&q=80&w=400',
  shoes:
    'https://images.unsplash.com/photo-1549298916-b41d501d3772?auto=format&fit=crop&q=80&w=400',
  jacket:
    'https://images.unsplash.com/photo-1551028719-00167b16eac5?auto=format&fit=crop&q=80&w=400',
  perfume:
    'https://images.unsplash.com/photo-1541643600914-78b084683601?auto=format&fit=crop&q=80&w=400',
  giftset:
    'https://images.unsplash.com/photo-1594035910387-fea47794261f?auto=format&fit=crop&q=80&w=400',
  lotion:
    'https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&q=80&w=400',
  burgerMeal:
    'https://images.unsplash.com/photo-1610614819513-58e34989848b?auto=format&fit=crop&q=80&w=400',
  fries:
    'https://images.unsplash.com/photo-1573080496219-bb080dd4f877?auto=format&fit=crop&q=80&w=400',
  drink:
    'https://images.unsplash.com/photo-1581006852262-e4307cf6283a?auto=format&fit=crop&q=80&w=400',
  dessert:
    'https://images.unsplash.com/photo-1563805042-7684c019e1cb?auto=format&fit=crop&q=80&w=400',
  familyPack:
    'https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&q=80&w=400',
  bread:
    'https://images.unsplash.com/photo-1509440159596-0249088772ff?auto=format&fit=crop&q=80&w=400',
  dairy:
    'https://images.unsplash.com/photo-1550583724-b2692b85b150?auto=format&fit=crop&q=80&w=400',
  coffee:
    'https://images.unsplash.com/photo-1559056199-641a0ac8b55e?auto=format&fit=crop&q=80&w=400',
  villa:
    'https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&q=80&w=400',
  apartment:
    'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&q=80&w=400',
  furnish:
    'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&q=80&w=400',
  laptop:
    'https://images.unsplash.com/photo-1517336714731-489689fd1ca8?auto=format&fit=crop&q=80&w=400',
  headphones:
    'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?auto=format&fit=crop&q=80&w=400',
  warranty:
    'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&q=80&w=400',
}

const c = (
  id: string,
  name: string,
  price: string,
  note: string,
  image: string,
  reason: string,
): RecoCandidate => ({ id, name, price, note, image, reason })

/** Higher-value alternatives, per trade. */
export const BETTER_OPTIONS: Record<string, RecoCandidate[]> = {
  'vehicle-rental': [
    c(
      'b-lc',
      'Toyota Land Cruiser 2025',
      'AED 7,900 / month',
      'Luxury SUV · 7 seats',
      IMG.landcruiser,
      'Recommend this when the customer wants more space, comfort or a more premium SUV.',
    ),
    c(
      'b-patrol',
      'Nissan Patrol 2025',
      'AED 6,400 / month',
      'Large SUV · 8 seats',
      IMG.patrol,
      'Recommend this when the customer needs more seats and more power for desert or highway driving.',
    ),
  ],
  'vehicle-sale': [
    c(
      'b-g63',
      'Mercedes-Benz G 63 2024',
      'AED 975,000',
      'Luxury SUV · 12,400 km',
      IMG.landcruiser,
      'Recommend this when the customer is comparing on presence and resale value rather than price.',
    ),
    c(
      'b-patrol-sale',
      'Nissan Patrol Platinum 2025',
      'AED 289,000',
      'SUV · brand new',
      IMG.patrol,
      'Recommend this when the customer wants a newer vehicle with full warranty.',
    ),
  ],
  perfume: [
    c(
      'b-100',
      'Same fragrance · 100 ml',
      'AED 1,180',
      'Extrait de Parfum',
      IMG.perfume,
      'Recommend the larger bottle when the customer buys this scent regularly — it works out cheaper per millilitre.',
    ),
    c(
      'b-extrait',
      'Oud Royale Extrait',
      'AED 1,650',
      'Higher concentration',
      IMG.perfume,
      'Recommend this when the customer asks about longevity or projection.',
    ),
  ],
  clothing: [
    c(
      'b-premium-shirt',
      'Premium Irish Linen Shirt',
      'AED 620',
      'Shirts · heavier weave',
      IMG.shirt,
      'Recommend this when the customer cares about fabric quality or wants something that holds its shape longer.',
    ),
  ],
  restaurant: [
    c(
      'b-meal',
      'Make it a large meal',
      'AED 39',
      'Burger · fries · drink',
      IMG.burgerMeal,
      'Recommend the meal when the customer is ordering a single item — it is better value than adding a side separately.',
    ),
    c(
      'b-double',
      'Double patty',
      'AED 32',
      'Bigger portion',
      IMG.burgerMeal,
      'Recommend this when the customer mentions being hungry or ordering for one big appetite.',
    ),
  ],
  grocery: [
    c(
      'b-family',
      'Family pack',
      'AED 89',
      'Pack of 6 · 15% cheaper per unit',
      IMG.familyPack,
      'Recommend the larger pack when the customer buys this regularly or is shopping for a household.',
    ),
  ],
  'property-rent': [
    c(
      'b-villa',
      '4-Bed Villa · Arabian Ranches',
      'AED 265,000 / year',
      'Villa · private garden',
      IMG.villa,
      'Recommend this when the customer mentions children, pets or needing outdoor space.',
    ),
  ],
  'property-sale': [
    c(
      'b-penthouse',
      'Palm Jumeirah Penthouse',
      'AED 12,500,000',
      'Penthouse · sea view',
      IMG.apartment,
      'Recommend this when the customer is buying as an investment or asking about premium views.',
    ),
  ],
  electronics: [
    c(
      'b-pro',
      'Pro model · 512 GB',
      'AED 6,499',
      'Laptops · more memory',
      IMG.laptop,
      'Recommend this when the customer mentions video editing, heavy multitasking or keeping the device for years.',
    ),
  ],
  service: [
    c(
      'b-annual',
      'Annual maintenance plan',
      'AED 2,400 / year',
      'Four visits included',
      IMG.service,
      'Recommend the plan when the customer books a second visit — it costs less than paying per call-out.',
    ),
  ],
}

/** Useful extras, services and complements, per trade. */
export const ADD_ONS: Record<string, RecoCandidate[]> = {
  'vehicle-rental': [
    c(
      'a-airport',
      'Airport delivery',
      'AED 75',
      'Service',
      IMG.airport,
      'Useful for customers arriving at or leaving from an airport.',
    ),
    c(
      'a-seat',
      'Child seat',
      'AED 50',
      'Add-on',
      IMG.seat,
      'Relevant for families travelling with young children.',
    ),
    c(
      'a-driver',
      'Additional driver',
      'AED 25 / day',
      'Add-on',
      IMG.camry,
      'Useful on longer rentals or when a couple is sharing the driving.',
    ),
    c(
      'a-insurance',
      'Full insurance cover',
      'AED 45 / day',
      'Add-on',
      IMG.service,
      'Worth raising for first-time renters and long-distance trips.',
    ),
  ],
  'vehicle-sale': [
    c(
      'a-warranty',
      'Extended warranty · 2 years',
      'AED 7,500',
      'Service',
      IMG.warranty,
      'Useful for customers concerned about running costs after purchase.',
    ),
    c(
      'a-service-pack',
      'Service package · 3 years',
      'AED 5,200',
      'Service',
      IMG.service,
      'Relevant when the customer asks about maintenance.',
    ),
  ],
  perfume: [
    c(
      'a-giftset',
      'Discovery gift set',
      'AED 260',
      'Gift Sets',
      IMG.giftset,
      'Relevant when the customer is buying a present or trying the house for the first time.',
    ),
    c(
      'a-lotion',
      'Matching body lotion',
      'AED 190',
      'Body',
      IMG.lotion,
      'Useful for customers who want the scent to last longer on the skin.',
    ),
    c(
      'a-travel',
      'Travel atomiser · 10 ml',
      'AED 120',
      'Travel',
      IMG.perfume,
      'Handy for customers who travel or want to carry the scent with them.',
    ),
  ],
  clothing: [
    c(
      'a-belt',
      'Leather belt',
      'AED 240',
      'Accessories',
      IMG.belt,
      'Completes the outfit and is often bought alongside shirts and trousers.',
    ),
    c(
      'a-shoes',
      'Leather court sneakers',
      'AED 690',
      'Shoes',
      IMG.shoes,
      'Relevant for customers putting together a smart-casual look.',
    ),
    c(
      'a-jacket',
      'Suede bomber jacket',
      'AED 1,450',
      'Outerwear',
      IMG.jacket,
      'Worth raising for cooler evenings and layered outfits.',
    ),
  ],
  restaurant: [
    c(
      'a-fries',
      'French fries',
      'AED 9',
      'Sides',
      IMG.fries,
      'The most commonly added side with this dish.',
    ),
    c(
      'a-drink',
      'Soft drink',
      'AED 8',
      'Drinks',
      IMG.drink,
      'Almost always ordered with a main — worth offering once.',
    ),
    c(
      'a-dessert',
      'Oreo sundae',
      'AED 12',
      'Desserts',
      IMG.dessert,
      'Relevant at the end of the order, especially for families.',
    ),
  ],
  grocery: [
    c(
      'a-bread',
      'Sourdough loaf',
      'AED 18',
      'Bakery',
      IMG.bread,
      'Frequently bought together with this item.',
    ),
    c(
      'a-dairy',
      'Full-fat laban 1L',
      'AED 9',
      'Dairy',
      IMG.dairy,
      'A common basket companion for this category.',
    ),
    c(
      'a-coffee',
      'Coffee beans 1 kg',
      'AED 72',
      'Pantry',
      IMG.coffee,
      'Worth raising for customers doing a weekly shop.',
    ),
  ],
  'property-rent': [
    c(
      'a-furnish',
      'Furnishing package',
      'AED 18,000',
      'Service',
      IMG.furnish,
      'Useful for customers relocating or renting short-term.',
    ),
    c(
      'a-maintenance',
      'Annual maintenance cover',
      'AED 3,600',
      'Service',
      IMG.service,
      'Relevant for tenants who ask who handles repairs.',
    ),
  ],
  'property-sale': [
    c(
      'a-mortgage',
      'Mortgage arrangement',
      'Free consultation',
      'Service',
      IMG.service,
      'Useful when the customer asks about payment plans or finance.',
    ),
    c(
      'a-management',
      'Property management',
      '5% of annual rent',
      'Service',
      IMG.villa,
      'Relevant for buyers purchasing as an investment.',
    ),
  ],
  electronics: [
    c(
      'a-headphones',
      'Wireless headphones',
      'AED 899',
      'Audio',
      IMG.headphones,
      'Commonly bought alongside this device.',
    ),
    c(
      'a-care',
      'Accidental damage cover',
      'AED 499 / year',
      'Service',
      IMG.warranty,
      'Worth raising for customers buying for travel or students.',
    ),
  ],
  service: [
    c(
      'a-callout',
      'Priority call-out',
      'AED 150',
      'Add-on',
      IMG.service,
      'Useful for customers with an urgent problem.',
    ),
    c(
      'a-parts',
      'Parts and consumables',
      'From AED 80',
      'Add-on',
      IMG.service,
      'Worth mentioning so the quote holds no surprises.',
    ),
  ],
}

/** What a merchant would plausibly already have switched on. */
export const SEEDED: Record<string, { better: string[]; addons: string[] }> = {
  'vehicle-rental': {
    better: ['b-lc'],
    addons: ['a-airport', 'a-seat', 'a-driver'],
  },
  restaurant: {
    better: ['b-meal'],
    addons: ['a-fries', 'a-drink'],
  },
  perfume: {
    better: ['b-100'],
    addons: ['a-giftset'],
  },
}

const fallbackBetter = BETTER_OPTIONS.grocery
const fallbackAddons = ADD_ONS.grocery

export function betterOptionsFor(kind: string): RecoCandidate[] {
  return BETTER_OPTIONS[kind] ?? fallbackBetter
}

export function addOnsFor(kind: string): RecoCandidate[] {
  return ADD_ONS[kind] ?? fallbackAddons
}

/** The recommendations a merchant starts with for this kind of item. */
export function seedRecos(kind: string): Reco[] {
  const seed = SEEDED[kind]
  if (!seed) return []
  return [
    ...betterOptionsFor(kind)
      .filter((o) => seed.better.includes(o.id))
      .map((o) => ({ ...o, kind: 'better' as RecoKind, active: true })),
    ...addOnsFor(kind)
      .filter((o) => seed.addons.includes(o.id))
      .map((o) => ({ ...o, kind: 'addon' as RecoKind, active: true })),
  ]
}

export type RecoStyle = 'Subtle' | 'Balanced' | 'Proactive'

export const RECO_STYLES: { id: RecoStyle; help: string }[] = [
  {
    id: 'Subtle',
    help: 'Only suggest when clearly relevant.',
  },
  {
    id: 'Balanced',
    help: 'Suggest naturally when it helps the customer.',
  },
  {
    id: 'Proactive',
    help: 'Actively look for useful recommendations.',
  },
]
